import React, { FC, useContext, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import { Section } from 'src/components/section';
import { userDataKeys } from 'src/features/details-form/constants';
import { ControlledField } from 'src/components/controlled-field';
import { FieldTypes, TextFieldTypes } from 'src/components/controlled-field/types';
import type { UserDataFormValues } from 'src/features/details-form/types';
import { Button } from 'src/components/button';
import { LoginDialog } from 'src/features/login-dialog';

import { AuthContext } from '../../../providers/auth-context';

import { styles } from './styles';

import type { EmailFormProps } from './types';

export const EmailForm: FC<EmailFormProps<UserDataFormValues>> = ({
    control,
    onContinue,
    showAdditionalFields,
    showCta,
    isEmailValid,
    isUserExistsPending,
}) => {
    const { t } = useTranslation();

    const userState = useContext(AuthContext);
    const [isLoginDialogOpen, setIsLoginDialogOpen] = useState<boolean>(false);

    const disableImmutableFields = userState.loggedIn;
    const {
        watch,
        formState: { errors },
    } = useFormContext();

    const email = watch(userDataKeys.email);

    useEffect(() => {
        if (Object.keys(errors).length) {
            const firstErrorField = document.querySelector('.Mui-error');
            if (firstErrorField) {
                firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [errors]);

    const handleLoginClick = () => {
        setIsLoginDialogOpen(!userState.loggedIn);
    };

    const handleCloseLoginDialog = () => {
        setIsLoginDialogOpen(false);
    };

    if (userState.loggedIn) {
        return null;
    }

    return (
        <Section header={t('your_details_create_an_account')} style={styles.wrapper}>
            <ControlledField
                type={TextFieldTypes.EMAIL}
                name={userDataKeys.email}
                control={control}
                label={t('your_details_form_email_label')}
                placeholder={t('your_details_form_email_placeholder')}
                disabled={disableImmutableFields}
                id="your-details-email"
            />
            {showAdditionalFields && (
                <div style={{ display: 'contents' }}>
                    <Typography variant="body4" css={styles.userMessage}>
                        <Trans
                            i18nKey="your_details_form_user_exists_message"
                            components={{
                                span: (
                                    <Typography
                                        component="span"
                                        css={styles.loginLink}
                                        onClick={handleLoginClick}
                                    />
                                ),
                            }}
                        />
                    </Typography>
                    <Box css={styles.inputGroupWrapper}>
                        <ControlledField
                            fieldType={FieldTypes.Password}
                            name={userDataKeys.password}
                            control={control}
                            label={t('your_details_form_create_password_label')}
                            placeholder={t('your_details_form_create_password_placeholder')}
                            id="your-details-password"
                        />
                        <ControlledField
                            fieldType={FieldTypes.Password}
                            name={userDataKeys.repeatPassword}
                            control={control}
                            label={t('your_details_form_repeat_password_label')}
                            placeholder={t('your_details_form_repeat_password_placeholder')}
                            id="your-details-password-confirm"
                        />
                    </Box>
                </div>
            )}

            {showCta && (
                <Button
                    variant="contained"
                    css={styles.cta}
                    disabled={!isEmailValid}
                    loading={isUserExistsPending}
                    onClick={onContinue}
                    id="your-details-email-submit"
                >
                    {t('your_details_cta')}
                </Button>
            )}

            <LoginDialog
                open={isLoginDialogOpen}
                closeFn={handleCloseLoginDialog}
                currentEmail={email}
            />
        </Section>
    );
};
